import { LazyLoader } from '@bp/ui-components';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableSolverEngines } from '../../hooks/useAvailableSolverEngines';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../hooks/useUserConfigContext';
import {
  availabilityStore,
  timeAvailabilityStoreHandler,
} from '../../pages/Timetable/Plan/TimetableVersion/TimetableVersion';
import { StoreHandler } from '../../stores/StoreHandler';
import { PinboardStore } from '../../stores/TimetableVersion/PinboardStore';
import { DayItem } from '../../stores/TimetableVersion/PinboardStoreTypes';
import { use_RoomsQuery, useTimetableVersionDataQuery } from '../../types/planung-graphql-client-defs';
import { buildCardsFromTimetableVersionData } from '../../utils/buildCardsFromTimetableVersionData';
import { isNotEmpty } from '../../utils/typeguards';
import { claimVersionData, markVersionDataApplied } from './versionDataSync';

const timetableStoreHandler = new StoreHandler<PinboardStore>();
const TimetableStoreContext = createContext<PinboardStore>({} as PinboardStore);

type TimetableProviderProps = {
  versionUuid: string;
} & PropsWithChildren;

export const TimetableProvider: FC<TimetableProviderProps> = ({ versionUuid, children }) => {
  const userConfig = useUserConfigContext();
  const selectedSchoolYear = userConfig.selectedSchoolYear;
  const { t } = useTranslation();
  const permissions = usePermissionChecker();

  // cache-and-network: Beim (erneuten) Betreten der Version zeigt das Board sofort den gecachten Store,
  // holt aber den Stand vom Server nach. Die Board-Mutationen (Stecken, Entfernen, Sperren, Räume)
  // liefern nur Skalare und verwerfen diese Query daher nie; mit cache-first bliebe der urql-Cache
  // auf dem Stand des ersten Ladens stehen.
  const [{ data: versionData }, reexecuteVersionData] = useTimetableVersionDataQuery({
    variables: { versionUuid },
    pause: !versionUuid,
    requestPolicy: 'cache-and-network',
  });

  // `view.rooms` (TimetableVersionRoom) kennt kein Gebäude — das kommt aus der Raum-Query dazu.
  const roomContext = useMemorizedCacheTag('ROOMS');
  const [{ data: roomsData }] = use_RoomsQuery({ context: roomContext });

  const data = versionData?.timetableVersionData;
  const meta = data?.meta;
  const view = data?.view;
  const editor = data?.editor;

  const timeAvailabilityStore = timeAvailabilityStoreHandler.get(versionUuid ?? '');

  const availableSolverEngines = useAvailableSolverEngines();

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  const timetableStore = useMemo(() => {
    if (!meta || !view || !selectedSchoolYear) return null;

    return timetableStoreHandler.register(meta.versionUuid, () => {
      const days: DayItem[] = [
        { value: 'mon', label: t('days.monday') },
        { value: 'tue', label: t('days.tuesday') },
        { value: 'wed', label: t('days.wednesday') },
        { value: 'thu', label: t('days.thursday') },
        { value: 'fri', label: t('days.friday') },
      ];

      const timeGridEntries = [...view.timeGridEntries].sort((a, b) => a.order - b.order);

      const store = new PinboardStore({
        schoolyear: selectedSchoolYear.uuid ?? null,
        currentVersion: {
          uuid: meta.versionUuid,
          versionName: `${meta.version}`,
          description: meta.description ?? '',
          timetableName: meta.timetableName,
          version: meta.version,
          active: meta.active,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        timeGridEntries: timeGridEntries as any,
        days,
        readonly: !permissions?.canWriteTimetable()
          ? true
          : isNotEmpty(meta.editable)
            ? !meta.editable
            : meta.active || meta.isSnapshot || false,
        temporarilyEditable: (meta.active && meta.editable) ?? false,
        userConfig,
      });

      if (timeAvailabilityStore) {
        for (const avail of view.availabilities) {
          const type = avail.resourceType.toLowerCase() as 'class' | 'teacher' | 'room' | 'subject';
          const props = {
            mon: avail.mon,
            tue: avail.tue,
            wed: avail.wed,
            thu: avail.thu,
            fri: avail.fri,
            sat: avail.sat,
            sun: avail.sun,
          };
          availabilityStore.setAvailability(type, avail.resourceUuid, props);
          timeAvailabilityStore.setResourceAvailability([{ uuid: avail.resourceUuid, days: props }], type);
        }
      }

      const cards = buildCardsFromTimetableVersionData(view, editor ?? null);

      // Grid types are slim subsets of the fat store types — runtime usage is safe
      // view.classes enthält ALLE SchoolYear-Klassen (inVersion-Flag fürs Import-Delta),
      // das Grid zeigt nur die importierten Klassen der Version
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      store.initData({
        cards,
        classes: view.classes.filter((c) => c.inVersion) as unknown as Parameters<typeof store.initData>[0]['classes'],
        rooms: view.rooms.map((room) => ({
          ...room,
          building: roomsData?.rooms.find((r) => r.uuid === room.uuid)?.building ?? null,
        })) as unknown as Parameters<typeof store.initData>[0]['rooms'],
        teachers: view.teachers as unknown as Parameters<typeof store.initData>[0]['teachers'],
        subjects: view.subjects as unknown as Parameters<typeof store.initData>[0]['subjects'],
      });

      // Erst jetzt, nach initData: Die Kartenlogik setzt den Kontext beim Laden
      // selbst und wuerde den gespeicherten Wert sonst ueberschreiben.
      store.restorePersistedContext();
      markVersionDataApplied(store, view);

      return store;
    });
  }, [meta, view, editor, selectedSchoolYear, timeAvailabilityStore, permissions, roomsData, userConfig]);

  // Refresh-Weg: Store bekommt einen Callback, der die bestehende TimetableVersionData-Query
  // neu ausführt (network-only). Genutzt nach Solver-Ende und Lesson-Delete – kein separater Loader.
  useEffect(() => {
    timetableStore?.setReloadVersionData(() => reexecuteVersionData({ requestPolicy: 'network-only' }));
  }, [timetableStore, reexecuteVersionData]);

  // Frische Query-Daten (nach reloadVersionData oder beim erneuten Betreten) in den Store spiegeln.
  // Ein Ergebnis, aus dem der Store schon befüllt wurde (initData oben, oder das gecachte beim
  // erneuten Betreten), wird übersprungen – es würde seine optimistischen Änderungen zurückdrehen.
  useEffect(() => {
    if (!timetableStore || !view) return;
    if (!claimVersionData(timetableStore, view)) return;
    timetableStore.initCards(buildCardsFromTimetableVersionData(view, editor ?? null));
  }, [timetableStore, view, editor]);

  useEffect(() => {
    if (!timetableStore || !versionUuid) {
      return;
    }
    void timetableStore.solverStore.resumeActiveJobForTimetableVersion(versionUuid);
  }, [timetableStore, versionUuid]);

  useEffect(() => {
    timetableStore?.solverStore.setAvailableEngines(availableSolverEngines);
  }, [timetableStore, availableSolverEngines]);

  return (
    <>
      {timetableStore ? (
        <TimetableStoreContext.Provider value={timetableStore}>{children}</TimetableStoreContext.Provider>
      ) : (
        <LazyLoader transparent forceHeight='60vh' />
      )}
    </>
  );
};

export const useTimetableStore = () => useContext(TimetableStoreContext);
